import type { FragmentData } from "@gqlb/core";
import { Check } from "lucide-react";
import NextLink from "next/link";
import type React from "react";
import { b } from "@/b";
import { CURRENCY_FRAGMENT, CurrencyRenderer } from "@/components/CurrencyRenderer";
import { MEDIA_FRAGMENT, ResponsiveImage } from "@/components/ResponsiveImage";

export const ProductCard_Fragment = b.fragment("ProductCard", "Product", (b) => [
  //
  b.id(),
  b.name(),
  b.slug(),
  b.price((b) => [b.__fragment(CURRENCY_FRAGMENT)]),
  b.availability((b) => [b.availableFrom((b) => [b.iso8601()])]),
  b.vipPrice((b) => [b.__fragment(CURRENCY_FRAGMENT)]),
  b.viewerPrice((b) => [b.__fragment(CURRENCY_FRAGMENT)]),
  b.images((b) => [b.__fragment(MEDIA_FRAGMENT)]),
]);

export const ProductCard: React.FC<{
  data: FragmentData<typeof ProductCard_Fragment>;
  className?: string;
  callout?: string;
  onClick?: () => void;
  inCart?: boolean;
  lastViewed?: boolean;
}> = ({ data: p, className, callout, onClick, inCart, lastViewed }) => {
  const hasDiscount = p.viewerPrice.value !== p.price.value;

  const sharedClassName = `@container group relative w-full overflow-hidden rounded-xl @sm:rounded-2xl no-underline cursor-pointer ${className ?? ""}`;

  const content = (
    <div className="relative aspect-[4/3] overflow-hidden bg-[#f0f0f0] rounded-xl @sm:rounded-2xl">
      {/* Zoomable image layer */}
      <div className="absolute inset-0 transition-transform duration-500 ease-out group-hover:scale-105">
        {p.images.length > 0 && (
          <>
            <ResponsiveImage
              image={p.images[0]}
              objectFit="cover"
              className="absolute inset-0 w-full h-full"
            />
            {p.images[1] && (
              <div className="absolute inset-0 opacity-0 transition-opacity duration-400 ease-out group-hover:opacity-100">
                <ResponsiveImage
                  image={p.images[1]}
                  objectFit="cover"
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom gradient overlay */}
      <div
        className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 40%, transparent 100%)",
        }}
      />

      {/* Price — top right */}
      {p.viewerPrice.value > 0 && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur-sm px-2.5 py-0.5 text-[11px] @sm:text-xs @lg:text-sm">
          {hasDiscount && (
            <span className="text-white/50 line-through">
              <CurrencyRenderer data={p.price} />
            </span>
          )}
          <span
            className={hasDiscount ? "text-brand-mint font-semibold" : "text-white font-medium"}
          >
            <CurrencyRenderer data={p.viewerPrice} />
          </span>
        </div>
      )}

      {/* Top-left badge */}
      {lastViewed ? (
        <span className="absolute top-3 left-3 z-10 inline-flex items-center rounded-full bg-white/90 backdrop-blur-sm px-2.5 py-0.5 text-[11px] @sm:text-xs font-medium text-gray-600">
          Recently viewed
        </span>
      ) : callout ? (
        <span className="absolute top-3 left-3 z-10 inline-flex items-center rounded-full bg-brand-mint px-2.5 py-0.5 text-[11px] @sm:text-xs @lg:text-sm font-semibold uppercase tracking-wide text-brand-black">
          {callout}
        </span>
      ) : null}

      {/* Name & cart indicator / chevron overlaid at bottom */}
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between gap-2 p-3 @lg:p-4">
        <p className="text-sm @sm:text-base @lg:text-lg font-medium text-white leading-snug line-clamp-2 text-left">
          {p.name}
        </p>
        {inCart && (
          <span className="hidden @sm:flex items-center gap-1 rounded-full bg-brand-mint px-2 py-0.5 text-[11px] @sm:text-xs font-semibold text-brand-black shrink-0">
            <Check className="size-3 @sm:size-3.5" strokeWidth={3} />
            In Cart
          </span>
        )}
      </div>
    </div>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={sharedClassName}>
        {content}
      </button>
    );
  }

  return (
    <NextLink href={`/product/${p.slug}`} className={sharedClassName}>
      {content}
    </NextLink>
  );
};
